import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { options } from "../../../../queryParamsToOptions";
import { withTranslation } from "../../../../../translation";
import { getSortOverlayVisible } from "../../../../../../state/modules/layout/selectors";
import { hideSortOverlay } from "../../../../../../state/modules/layout/actions";
import { getLocation } from "../../../../../../state/modules/router/selectors";
import { getPageName } from "../../../../../../state/modules/page/selectors";
import { changeSort } from "../../../../../../state/modules/search/actions";
import classnames from "classnames";
import styles from "./index.css";
import { useState, useRef, useEffect } from "react";

export const SortList = ({ formatTranslation }) => {
  const dispatch = useDispatch();
  const location = useSelector(state => getLocation(state));
  const isVisible = useSelector(state => getSortOverlayVisible(state));
  const pageName = useSelector(state => getPageName(state));
  if (!isVisible) {
    return null;
  }

  const styleContext = classnames.bind(styles);
  const sortOptions = options(pageName);
  const selectedIdx = sortOptions.findIndex(
    ({ value }) => location.query.sort === value
  );
  const [focusedIdx, setFocusedIdx] = useState(
    selectedIdx >= 0 ? selectedIdx : 0
  );
  const radioRefs = useRef([]);

  useEffect(() => {
    if (radioRefs.current[focusedIdx]) {
      radioRefs.current[focusedIdx].focus();
    }
  }, [focusedIdx]);

  const handleClick = (idx, sortedBy, translatedLabel) => () => {
    dispatch(changeSort({ type: sortedBy, name: translatedLabel }));
    setFocusedIdx(idx);
  };
  const ARROW_KEYS = {
    ArrowLeft: -1,
    ArrowUp: -1,
    ArrowRight: 1,
    ArrowDown: 1
  };
  const handleKeyDown = (event, idx) => {
    const move = ARROW_KEYS[event.key];
    if (move) {
      const nextIdx = (idx + move + sortOptions.length) % sortOptions.length;
      dispatch(
        changeSort({
          type: sortOptions[nextIdx].value,
          name: formatTranslation(sortOptions[nextIdx].label)
        })
      );
      setFocusedIdx(nextIdx);
      event.preventDefault();
      setTimeout(() => {
        radioRefs.current[nextIdx]?.focus();
      }, 0);
    }
  };

  const handleClose = () => {
    dispatch(hideSortOverlay());
  };

  return (
    <div
      className={styles.refinementList}
      aria-label={formatTranslation("plp_web_sort_sort")}
      role="radiogroup"
    >
      <div
        className={classnames(
          styleContext({ navigation: true }),
          styles.navigation
        )}
      >
        <div className={styles.primaryList}>
          <header className={styles.refinementsHeader}>
            <div className={styles.topLine}>
              <h3 className={styles.refinementsTitle}>
                {formatTranslation("plp_web_sort_sort")}
              </h3>
              <button
                className={styles.close}
                onClick={handleClose}
                aria-label={formatTranslation("accessibility_label_close")}
              />
            </div>
          </header>
          <div
            className={classnames(
              styles.refinementsListWrapper,
              styles.unfilterableRefinementsListWrapper
            )}
          >
            <ul className={styles.refinementsList}>
              {sortOptions.map(({ value, label }, index) => {
                const isSelected = location.query.sort === value;

                return (
                  <li
                    className={classnames(styles.facet, styles.button)}
                    key={index}
                  >
                    <label htmlFor={label}>
                      <input
                        ref={function (el) {
                          radioRefs.current[index] = el;
                        }}
                        tabIndex={isSelected ? 0 : -1}
                        aria-label={formatTranslation(label)}
                        type="radio"
                        name="sortList"
                        checked={location.query.sort === value}
                        id={label}
                        value={value}
                        onClick={handleClick(
                          index,
                          value,
                          formatTranslation(label)
                        )}
                        onKeyDown={e => handleKeyDown(e, index)}
                        onChange={() => {}}
                      />

                      {formatTranslation(label)}
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
          <footer className={styles.footer}>
            <button
              className={styles.viewItemsBtn}
              aria-label={formatTranslation(
                "plp_web_refinements_list_view_items"
              )}
              onClick={handleClose}
            >
              {formatTranslation("plp_web_refinements_list_view_items")}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};

SortList.propTypes = {
  formatTranslation: PropTypes.func.isRequired
};

SortList.displayName = "SortList";

export default withTranslation(SortList);
